/**
 * Shared HTTP response utilities for API clients.
 * Reduces duplication of body reading, size limiting, and 429 detection.
 */

import { request } from "undici";
import { ApiError, RateLimitError } from "./errors.js";
import { logger } from "./logger.js";

const DEFAULT_MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB

/** Options for {@link requestWithRetry}. */
export interface RetryOptions {
  /** Total attempts including the first (default 3). */
  maxAttempts?: number;
  /** Backoff base when the response has no usable Retry-After (default 1000ms → 1s, 2s). */
  baseDelayMs?: number;
  /**
   * Longest single wait we'll honor (default 10s). If the server asks for more,
   * we don't retry — the 429 surfaces as RateLimitError so the caller/user decides.
   */
  maxDelayMs?: number;
  /** Retry methods other than GET. Off by default — writes are not assumed idempotent. */
  retryNonIdempotent?: boolean;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Parse a Retry-After header value (delta-seconds or HTTP-date) to milliseconds. */
function parseRetryAfterMs(value: string | string[] | undefined): number | undefined {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const dateMs = Date.parse(raw);
  return Number.isNaN(dateMs) ? undefined : Math.max(0, dateMs - Date.now());
}

/**
 * `undici.request` with transparent retry on 429 for idempotent GETs (#7).
 *
 * Honors Retry-After (seconds or HTTP-date, capped at `maxDelayMs`), falls back to
 * exponential backoff, and gives up after `maxAttempts` — the final 429 response is
 * returned unconsumed so the existing readResponseBody → RateLimitError path fires.
 * Non-GET requests pass through in a single attempt unless `retryNonIdempotent` is set.
 */
export async function requestWithRetry(
  url: string,
  options: Parameters<typeof request>[1],
  retry: RetryOptions = {}
): Promise<Awaited<ReturnType<typeof request>>> {
  const maxAttempts = retry.maxAttempts ?? 3;
  const baseDelayMs = retry.baseDelayMs ?? 1000;
  const maxDelayMs = retry.maxDelayMs ?? 10_000;
  const sleep = retry.sleep ?? defaultSleep;
  const method = options?.method ?? "GET";
  const retryable = method === "GET" || retry.retryNonIdempotent === true;

  let attempt = 1;
  for (;;) {
    const response = await request(url, options);
    if (response.statusCode !== 429 || !retryable || attempt >= maxAttempts) {
      return response;
    }
    const delayMs = parseRetryAfterMs(response.headers["retry-after"]) ?? baseDelayMs * 2 ** (attempt - 1);
    if (delayMs > maxDelayMs) {
      // Server wants a longer pause than we're willing to block a tool call for.
      return response;
    }
    await response.body.text().catch(() => undefined); // drain so the socket is reusable
    logger.debug({ url, attempt, delayMs }, "429 received — retrying after backoff");
    await sleep(delayMs);
    attempt += 1;
  }
}

/**
 * Read and return the response body as a string, with 429 detection and size limits.
 * Throws RateLimitError on 429, ApiError if body exceeds maxSize.
 */
export async function readResponseBody(
  response: Awaited<ReturnType<typeof request>>,
  context: string,
  maxSize = DEFAULT_MAX_RESPONSE_SIZE
): Promise<{ statusCode: number; text: string }> {
  const { statusCode, body } = response;

  // Handle 429 before reading body
  if (statusCode === 429) {
    await body.text();
    const retryAfter = response.headers["retry-after"];
    const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
    throw new RateLimitError(retryMs);
  }

  // Stream-read body with size limit to prevent OOM
  const chunks: Buffer[] = [];
  let totalSize = 0;
  for await (const chunk of body as AsyncIterable<Uint8Array>) {
    totalSize += chunk.length;
    if (totalSize > maxSize) {
      throw new ApiError(
        `${context}: response too large (max ${Math.round(maxSize / 1024 / 1024)}MB)`,
        413
      );
    }
    chunks.push(Buffer.from(chunk));
  }

  return { statusCode, text: Buffer.concat(chunks).toString("utf-8") };
}

/**
 * Parse response text as JSON, returning a safe empty value for empty responses.
 */
export function parseResponseBody<T>(text: string): T {
  if (!text) {
    return { value: [] } as unknown as T;
  }
  return JSON.parse(text) as T;
}
